import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { fragmentContainsNodeType } from '@atlaskit/editor-common/insert';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { eventHub } from '../../../../pm-plugins/ai-event-hub/event-hub';
import { findEditorIdFromView } from '../../../../pm-plugins/ai-event-hub/utils/isolated-utils';
import { beforeDispatchAIRovoAgentTransaction, beforeDispatchAITransaction, beforeDispatchingTransactionForBlocks, insertContentAfterTable, insertAIContentAtCurrentPosition, replaceWithAIContent, replaceWithAIContentForBlock, shouldInsertAfterTable } from '../utils/action-utils';
export var createInsertAtTop = function createInsertAtTop(_ref) {
  var appearance = _ref.appearance;
  return {
    type: 'markdown',
    title: aiConfigItemMessages.configActionInsertAtTop,
    appearance: appearance,
    run: function run(_ref2) {
      var editorView = _ref2.editorView,
        pmFragment = _ref2.pmFragment;
      var state = editorView.state;
      var transaction = state.tr;
      var slice = new Slice(pmFragment, 0, 0);
      transaction.replace(0, 0, slice);
      beforeDispatchAITransaction(transaction, state);
      editorView.dispatch(transaction);
    }
  };
};
export var createInsertBelow = function createInsertBelow(_ref3) {
  var appearance = _ref3.appearance;
  return {
    type: 'markdown',
    title: aiConfigItemMessages.configActionInsertBelow,
    appearance: appearance,
    run: function run(_ref4) {
      var editorView = _ref4.editorView,
        positions = _ref4.positions,
        pmFragment = _ref4.pmFragment,
        triggeredFor = _ref4.triggeredFor;
      var state = editorView.state;
      var transaction = state.tr;
      if (fragmentContainsNodeType(pmFragment, state.schema.nodes.table) && shouldInsertAfterTable({
        selection: state.selection,
        tableSchema: state.schema.nodes.table
      }) && fg('platform_editor_use_nested_table_pm_nodes')) {
        insertContentAfterTable({
          pmFragment: pmFragment,
          transaction: transaction
        });
      } else {
        if (triggeredFor !== null && triggeredFor !== void 0 && triggeredFor.isBlock) {
          var insertPos = positions[1];
          safeInsert(pmFragment, insertPos)(transaction);
          beforeDispatchingTransactionForBlocks(pmFragment, transaction, editorView.state);
        } else {
          var _insertPos;
          try {
            _insertPos = state.doc.resolve(positions[1]).after();
          } catch (error) {
            _insertPos = positions[1];
          }
          safeInsert(pmFragment, _insertPos)(transaction);
        }
      }
      beforeDispatchAITransaction(transaction, state);
      editorView.dispatch(transaction);
    }
  };
};
export var createInsertAtCurrentPosition = function createInsertAtCurrentPosition(_ref5) {
  var appearance = _ref5.appearance,
    isRovoAgentAction = _ref5.isRovoAgentAction,
    _ref5$title = _ref5.title,
    title = _ref5$title === void 0 ? aiConfigItemMessages.configActionInsert : _ref5$title;
  return {
    type: 'markdown',
    title: title,
    appearance: appearance,
    isRovoAgentAction: isRovoAgentAction,
    run: function run(_ref6) {
      var editorView = _ref6.editorView,
        positions = _ref6.positions,
        pmFragment = _ref6.pmFragment;
      var tr = insertAIContentAtCurrentPosition({
        editorView: editorView,
        positions: positions,
        pmFragment: pmFragment
      });
      if (isRovoAgentAction) {
        beforeDispatchAIRovoAgentTransaction(tr);
      }
      editorView.dispatch(tr);
    }
  };
};
export var createReplace = function createReplace(_ref7) {
  var appearance = _ref7.appearance,
    isRovoAgentAction = _ref7.isRovoAgentAction;
  return {
    type: 'markdown',
    title: aiConfigItemMessages.configActionReplace,
    appearance: appearance,
    isRovoAgentAction: isRovoAgentAction,
    run: function run(_ref8) {
      var editorView = _ref8.editorView,
        positions = _ref8.positions,
        pmFragment = _ref8.pmFragment,
        triggeredFor = _ref8.triggeredFor;
      var tr = triggeredFor !== null && triggeredFor !== void 0 && triggeredFor.isBlock ? replaceWithAIContentForBlock({
        editorView: editorView,
        positions: positions,
        pmFragment: pmFragment,
        triggeredFor: triggeredFor
      }) : replaceWithAIContent({
        editorView: editorView,
        positions: positions,
        pmFragment: pmFragment
      });
      if (isRovoAgentAction) {
        beforeDispatchAIRovoAgentTransaction(tr);
      }
      editorView.dispatch(tr);
    }
  };
};

/**
 * EXPERIMENTAL: Util for experimental flow where 'Suggest title' in
 * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
 * This util allows replacing the title in Confluence.
 */
export var createReplaceExistingConfluenceTitle = function createReplaceExistingConfluenceTitle(_ref9) {
  var appearance = _ref9.appearance;
  return {
    type: 'markdown',
    title: aiConfigItemMessages.configActionReplaceTitle,
    appearance: appearance,
    getDisabledState: function getDisabledState(_ref10) {
      var _markdown$length;
      var markdown = _ref10.markdown,
        formatMessage = _ref10.formatMessage;
      var responseLength = (_markdown$length = markdown === null || markdown === void 0 ? void 0 : markdown.length) !== null && _markdown$length !== void 0 ? _markdown$length : 0;
      // Source: next/packages/editor-title/src/EditorTitle.tsx
      var MAX_TITLE_LENGTH = 255;
      if (responseLength >= MAX_TITLE_LENGTH) {
        return {
          isDisabled: true,
          tooltip: formatMessage(aiConfigItemMessages.configActionReplaceTitleTooLongDisabledTooltip, {
            maxLength: MAX_TITLE_LENGTH
          })
        };
      }
      return {
        isDisabled: false
      };
    },
    run: function run(_ref11) {
      var editorView = _ref11.editorView,
        pmFragment = _ref11.pmFragment;
      var fragmentToInsert = pmFragment;
      var textContent = '';
      fragmentToInsert.forEach(function (node) {
        textContent += node.textContent;
      });
      var confluenceTitle = document.querySelector('textarea[data-ai-suggest-title-target="true"]');
      if (confluenceTitle) {
        var sourceEditorId = findEditorIdFromView(editorView);
        if (sourceEditorId) {
          eventHub.publishFromEditor({
            event: 'replace title in confluence',
            data: {
              analyticSourceId: 'confluenceTitleToolbar',
              sourceEditorId: sourceEditorId,
              title: textContent
            }
          });
        }
        confluenceTitle.scrollIntoView();
      }
    }
  };
};